// src/components/InviteUser.js

import React, { useState, useContext, useRef } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import { addDoc, collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../utils/firebaseConfig';
import { logUserActivity } from '../utils/auditLogger';
import { toast } from 'react-toastify';
import { FiMail, FiUserPlus, FiClipboard, FiCopy } from 'react-icons/fi';
import ConfirmationModal from './ConfirmationModal';

const InviteUser = () => {
  const { currentUser, userName } = useContext(AuthContext);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('trainee');
  const [referenceCode, setReferenceCode] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: async () => {}
  });
  const modalRef = useRef(null);

  const generateReferenceCode = () => {
    return Math.random().toString(36).substring(2, 10).toUpperCase();
  };

  const handleCreateInvite = async () => {
    if (!email) {
      toast.error('Please enter an email address');
      return;
    }

    setLoading(true);

    try {
      // Check if an invite already exists with the same email
      const emailQuery = query(collection(db, 'invites'), where('email', '==', email));
      const emailSnapshot = await getDocs(emailQuery);

      if (!emailSnapshot.empty) {
        toast.error('An invite with this email already exists.');
        setLoading(false);
        return;
      }

      // Generate a unique reference code
      let newReferenceCode;
      let codeExists = true;
      while (codeExists) {
        newReferenceCode = generateReferenceCode();
        const codeQuery = query(collection(db, 'invites'), where('referenceCode', '==', newReferenceCode));
        const codeSnapshot = await getDocs(codeQuery);
        if (codeSnapshot.empty) {
          codeExists = false;
        }
      }

      await addDoc(collection(db, 'invites'), {
        email,
        role,
        referenceCode: newReferenceCode,
        used: false,
        createdAt: new Date(),
      });

      // Audit log: user invite creation
      await logUserActivity(
        currentUser?.uid || 'unknown',
        userName || currentUser?.displayName || currentUser?.email || currentUser?.uid || 'unknown',
        'INVITE_USER',
        { invitedEmail: email, invitedRole: role }
      );

      setReferenceCode(newReferenceCode);
      setMessage(`Invitation created for ${email}`);
      setEmail('');
      setRole('trainee');
      toast.success(`Invitation created for ${email}`);
    } catch (error) {
      console.error('Error creating invite: ', error);
      if (error.code === 'permission-denied') {
        toast.error('You do not have permission to create an invite.');
      } else {
        toast.error('Failed to create invitation. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCopyReferenceCode = async () => {
    if (!referenceCode) {
      toast.error('No reference code to copy');
      return;
    }

    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(referenceCode);
        toast.success('Reference code copied to clipboard');
      } catch (err) {
        console.error('Failed to copy: ', err);
        fallbackCopyTextToClipboard(referenceCode);
      }
    } else {
      fallbackCopyTextToClipboard(referenceCode);
    }
  };

  const fallbackCopyTextToClipboard = (text) => {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.top = '0';
    textArea.style.left = '0';
    textArea.style.position = 'fixed';

    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      const successful = document.execCommand('copy');
      if (successful) {
        toast.success('Reference code copied to clipboard');
      } else {
        toast.error('Failed to copy reference code');
      }
    } catch (err) {
      console.error('Fallback: Oops, unable to copy', err);
      toast.error('Failed to copy reference code');
    }

    document.body.removeChild(textArea);
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    setConfirmModal({
      isOpen: true,
      title: 'Confirm Invitation',
      message: `Are you sure you want to invite ${email} as a ${role}?`,
      onConfirm: async () => {
        try {
          setLoading(true);
          await handleCreateInvite();
          setConfirmModal({ ...confirmModal, isOpen: false });
        } catch (error) {
          console.error('Error during invitation process:', error);
          toast.error('Failed to create invitation. Please try again.');
        } finally {
          setLoading(false);
        }
      }
    });

    // Ensure modal is visible after it opens with a longer timeout
    setTimeout(() => {
      if (modalRef.current) {
        const viewportHeight = window.innerHeight;
        const modalRect = modalRef.current.getBoundingClientRect();
        
        if (modalRect.top < 0 || modalRect.bottom > viewportHeight) {
          window.scrollTo({
            top: modalRect.top + window.scrollY - (viewportHeight - modalRect.height) / 2,
            behavior: 'smooth'
          });
        }
      }
    }, 300);
  };

  // Tailwind CSS styles
  const cardStyle = `p-4 sm:p-6 rounded-lg shadow-lg bg-primary-light dark:bg-primary-dark border border-secondary-light dark:border-secondary-dark transition-colors duration-300`;
  const inputStyle = `w-full p-3 rounded-md bg-secondary-light dark:bg-secondary-dark text-text-light dark:text-text-dark border border-secondary-light dark:border-secondary-dark focus:outline-none focus:ring-2 focus:ring-accent-light dark:focus:ring-accent-dark transition-colors duration-300`;
  const labelStyle = `block mb-2 font-semibold`;
  const buttonStyle = `w-full px-4 py-2 rounded-md bg-accent-light dark:bg-accent-dark text-primary-light dark:text-primary-dark hover:bg-accent hover:text-primary-light dark:hover:bg-accent-dark dark:hover:text-primary-dark transition-colors duration-300 mb-4`;

  return (
    <div className={cardStyle}>
      <h2 className="text-2xl font-semibold mb-6 flex items-center">
        <FiUserPlus className="mr-2" />
        Invite User
      </h2>
      <div className="mb-4">
        <label className={labelStyle}>Email:</label>
        <div className="flex flex-col sm:flex-row items-start sm:items-center">
          <FiMail className="mr-0 sm:mr-2 text-xl mb-2 sm:mb-0" />
          <input
            type="email"
            className={`${inputStyle} flex-1`}
            placeholder="Enter email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
      </div>
      <div className="mb-6">
        <label className={labelStyle}>Role:</label>
        <select
          className={inputStyle}
          value={role}
          onChange={(e) => setRole(e.target.value)}
        >
          <option value="trainee">Trainee</option>
          <option value="trainer">Trainer</option>
          <option value="admin">Admin</option>
        </select>
      </div>
      <button
        className={buttonStyle}
        onClick={handleCreateInvite}
        disabled={loading}
      >
        {loading ? 'Generating Invitation...' : 'Generate Invitation'}
      </button>
      {referenceCode && (
        <div className="mt-4">
          <p className="text-lg font-semibold mb-2 flex items-center">
            <FiClipboard className="mr-2" />
            Reference Code: {referenceCode}
          </p>
          <button
            className="w-full px-4 py-2 rounded-md bg-secondary-light dark:bg-secondary-dark text-text-light dark:text-text-dark hover:bg-accent-light dark:hover:bg-accent-dark hover:text-primary-light dark:hover:text-primary-dark transition-colors duration-300 flex items-center justify-center"
            onClick={handleCopyReferenceCode}
          >
            <FiCopy className="mr-2" />
            Copy Reference Code
          </button>
        </div>
      )}
      {message && (
        <div className="mt-4 text-green-500 dark:text-green-400">
          {message}
        </div>
      )}

      <ConfirmationModal
        ref={modalRef}
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ ...confirmModal, isOpen: false })}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
      />
    </div>
  );
};

export default InviteUser;
